import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Search, Wrench, Megaphone, TrendingUp, ExternalLink, BarChart3, Target, Zap, Globe, ArrowRight, Loader2, Linkedin } from "lucide-react";
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BadgesRow } from "@/components/BadgesRow";
import { ProfilesTabs } from "@/components/ProfilesTabs";
import { ServicesTriad } from "@/components/ServicesTriad";
import { OrganizationSEO } from "@/components/seo/OrganizationSEO";
import { HomeShortcutNav } from "@/components/HomeShortcutNav";
import { DestinationsNav } from "@/components/navigation/DestinationsNav";
import JsonLd from "@/components/JsonLd";
import HeroPlayButton from "@/components/HeroPlayButton";
import FAQAudioBlock from "@/components/FAQAudioBlock";
import PlayBadge from "@/components/PlayBadge";
import dashboardImage from '@/assets/amazon-dashboard-cosmos-wave.png';

// Animated Counter Component
function AnimatedCounter({
  end,
  duration = 2000,
  suffix = ""
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !isVisible) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => observer.disconnect();
  }, [isVisible]);
  useEffect(() => {
    if (!isVisible) return;
    const startTime = Date.now();
    const animateCount = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(end * easeOutQuart);
      setCount(currentCount);
      if (progress < 1) {
        requestAnimationFrame(animateCount);
      } else {
        setCount(end);
      }
    };
    animateCount();
  }, [isVisible, end, duration]);
  return <div ref={ref} className="text-3xl font-semibold text-primary font-['Helvetica_Neue',_-apple-system,_sans-serif]">
      {count}{suffix}
    </div>;
}

const orgJsonLd = {
  "@context":"https://schema.org",
  "@type":"Organization",
  "name":"Cosmos Wave",
  "url":"https://www.cosmos-wave.com",
  "logo":"https://www.cosmos-wave.com/assets/logo.png",
  "sameAs":["https://www.linkedin.com/company/cosmoswave"],
  "contactPoint":[{"@type":"ContactPoint","contactType":"sales","email":"contact@cosmos-wave.com","areaServed":["FR","EU"],"availableLanguage":["fr","en"]}],
  "founder":{"@type":"Person","name":"Karim Mesloub"},
  "knowsAbout":[
    "Amazon marketplace management","digital shelf optimization","Amazon Ads (SP/SB/SD/DSP)",
    "A+ Content","Amazon Store","TikTok Shop","Shopify","Listing SEO","Buy Box management","Marketplace operations & logistics"
  ],
  "slogan":"Agence Amazon-first : digital shelf, marketplaces et croissance pilotée par la data."
};

export default function CosmosWaveHome() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const { toast } = useToast();

  const handleDemoAccess = async () => {
    setIsDemoLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: 'demo@cosmos-wave.com',
        password: 'Demo2024!'
      });

      if (error) throw error;

      toast({
        title: "Connexion réussie",
        description: "Redirection vers votre dashboard...",
      });
      
      setTimeout(() => navigate('/espace-client'), 500);
    } catch (error: any) {
      toast({
        title: "Erreur de connexion",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDemoLoading(false);
    }
  };
  
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Écoute des événements audio pour changer l'icône
  useEffect(() => {
    const audio = document.getElementById('audio-methode') as HTMLAudioElement | null;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);
  
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.target as HTMLFormElement);
    const data = {
      nom: formData.get('nom') as string,
      email: formData.get('email') as string,
      societe: formData.get('societe') as string,
      message: formData.get('message') as string || ''
    };

    try {
      console.log('Submitting form data:', data);
      
      const { data: result, error } = await supabase.functions.invoke('send-contact-email', {
        body: data
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      console.log('Email sent successfully:', result);
      
      // Show success toast with calendar icon and countdown
      toast({
        title: "📧 Message envoyé avec succès !",
        description: "📅 Ouverture du calendrier de réservation dans quelques instants...",
      });

      // Reset form
      (e.target as HTMLFormElement).reset();
      
      // Show transition state with countdown
      let countdown = 3;
      const countdownToast = toast({
        title: "📅 Redirection en cours...",
        description: `Le calendrier s'ouvre dans ${countdown} secondes`,
      });
      
      const countdownInterval = setInterval(() => {
        countdown--;
        if (countdown > 0) {
          toast({
            title: "📅 Redirection en cours...",
            description: `Le calendrier s'ouvre dans ${countdown} seconde${countdown > 1 ? 's' : ''}`,
          });
        }
      }, 1000);
      
      // Redirect to cal.com after optimized delay
      setTimeout(() => {
        clearInterval(countdownInterval);
        window.open("https://cal.com/cosmoswave", "_blank");
        toast({
          title: "✅ Calendrier ouvert !",
          description: "Si la page ne s'est pas ouverte, veuillez vérifier les pop-ups bloquées.",
        });
      }, 3000);
      
    } catch (error: any) {
      console.error('Error submitting form:', error);
      
      toast({
        title: "Erreur d'envoi",
        description: "Une erreur s'est produite lors de l'envoi. Veuillez réessayer ou nous contacter directement.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <>
      <JsonLd data={orgJsonLd} />
      <div className="min-h-screen bg-background text-foreground">
        {/* Helmet Meta Tags */}
      <Helmet>
        <title>Cosmos Wave — Agence Amazon & IA (SPN)</title>
        <meta name="description" content="Agence Amazon & IA : Ads (SP/SB/SD/DSP), SEO listings & A+, Catalog Health/Buy Box, expansion EU. Méthode data-driven conforme SP-API. Audit gratuit en 48–72h." />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cosmos Wave" />
        <meta property="og:title" content="Cosmos Wave — Agence Amazon & IA (SPN)" />
        <meta property="og:description" content="Agence Amazon & IA : Ads (SP/SB/SD/DSP), SEO listings & A+, Catalog Health/Buy Box, expansion EU. Audit gratuit 48–72h." />
        <meta property="og:url" content="https://www.cosmos-wave.com/" />
        <meta property="og:image" content="https://www.cosmos-wave.com/og/home-1200x630.jpg" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cosmos Wave — Agence Amazon & IA (SPN)" />
        <meta name="twitter:description" content="Agence Amazon & IA : Ads (SP/SB/SD/DSP), SEO listings & A+, Catalog Health/Buy Box, expansion EU. Audit gratuit 48–72h." />
        <meta name="twitter:image" content="https://www.cosmos-wave.com/og/home-1200x630.jpg" />
      </Helmet>
      
      {/* SEO Meta Tags & Structured Data */}
      <OrganizationSEO
        title="Agence Amazon — Cosmos Wave | SPN & Creative Strategy"
        description="Agence Amazon data-driven : Ads (SP/SB/SD/DSP), SEO fiches, A+ & Store, Buy Box & Catalog Health. Audit gratuit et plan d'action priorisé."
        canonical="https://www.cosmos-wave.com/"
        ogImage="https://www.cosmos-wave.com/og-home.jpg"
        keywords="agence Amazon, Amazon SPN, Amazon Ads, partenaire Amazon, optimisation Amazon, fiches produits Amazon, A+ content, Amazon Store, DSP Amazon, creative strategy"
      />
      
      {/* Meta & Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Cosmos Wave",
        url: "https://cosmos-wave.com",
        logo: "https://cosmos-wave.com/logo.png",
        sameAs: ["https://www.linkedin.com/company/cosmoswave"],
        description: "Agence & Expert Amazon. Creative strategists en positionnement, branding et packaging."
      })
    }} />

      {/* Audio element */}
      <audio
        id="audio-methode"
        controls
        preload="metadata"
        crossOrigin="anonymous"
        playsInline
        src="https://symdojhcmtptpjipuwmp.supabase.co/storage/v1/object/public/media/faq-methode.mp3"
        className="sr-only"
      />

      {/* Badges Row */}
      <BadgesRow />

      {/* Play Button mobile - positionné sous les badges Amazon */}
      <div className="md:hidden flex justify-center py-6">
        <button
          type="button"
          data-audio-target="audio-methode"
          aria-label="Écouter l'essentiel : 5 min"
          className="relative select-none rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgba(255,106,0,0.55)]"
          style={{ width: "144px", height: "144px", WebkitTapHighlightColor: "transparent" }}
        >
          <div className="absolute inset-0 rounded-full grid place-items-center animate-cw-breathe">
            <div
              className="rounded-full grid place-items-center bg-[rgba(255,106,0,0.08)] border border-[rgba(255,106,0,0.35)]"
              style={{ width: "101px", height: "101px" }}
            >
              {isPlaying ? (
                <svg width="37" height="37" viewBox="0 0 24 24" fill="#FF6A00" aria-hidden="true">
                  <rect x="6" y="5" width="4" height="14" rx="1.2" />
                  <rect x="14" y="5" width="4" height="14" rx="1.2" />
                </svg>
              ) : (
                <svg width="37" height="37" viewBox="0 0 24 24" fill="#FF6A00" aria-hidden="true">
                  <polygon points="8,5 19,12 8,19" />
                </svg>
              )}
            </div>
          </div>
          <span className="absolute inset-0 rounded-full pointer-events-none" style={{ boxShadow: "0 0 0 1px rgba(255,106,0,.28)" }} />
        </button>
      </div>


      {/* Hero Section */}
      <section className="relative min-h-[70vh] xl:min-h-[80vh] 2xl:min-h-[75vh] flex items-center justify-center overflow-hidden py-12 xl:py-20">
        {/* Subtle background pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/50 to-background"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px), linear-gradient(hsl(var(--border)) 1px, transparent 1px)`,
          backgroundSize: '64px 64px'
        }}></div>
        </div>

        <div className="container mx-auto px-6 xl:px-8 2xl:px-12 z-10">
          <div className="max-w-7xl 2xl:max-w-8xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-8 md:gap-12 xl:gap-16 2xl:gap-20 items-center">
               {/* Image avec PlayBadge positionné en bas-droite sur desktop */}
               <div className="relative order-first lg:order-last xl:col-span-2 lg:-ml-4">
                  <div className="relative max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl 2xl:max-w-3xl mx-auto">
                   <img 
                     src="/lovable-uploads/66be0a67-df78-4902-9835-b764ae1137e4.png" 
                     alt="Astronaute femme représentant l'expertise créative et Amazon de Cosmos Wave - Agence Amazon SPN Paris" 
                     className="w-full h-auto object-contain transform hover:scale-105 transition-transform duration-700"
                     width="512"
                     height="512"
                     loading="eager"
                     fetchPriority="high"
                   />
                  <div className="absolute -inset-4 md:-inset-8 bg-gradient-to-r from-primary/20 to-transparent rounded-full blur-3xl opacity-50"></div>
                 </div>
              </div>
              
              {/* Content - Second sur mobile/tablette, premier sur desktop */}
              <div className="space-y-6 md:space-y-8 xl:space-y-10 xl:col-span-3 2xl:col-span-3 order-last lg:order-first">
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-3 md:gap-4 justify-center lg:justify-start">
                  <div className="inline-flex items-center px-4 md:px-6 py-2 md:py-3 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm md:text-base font-medium">
                    <span className="w-2 h-2 bg-primary rounded-full mr-2 md:mr-3 animate-pulse"></span>
                    Expertise Amazon & Créativité
                  </div>
                </div>
                
                
                {/* Title */}
                <div className="space-y-4 md:space-y-6 text-center lg:text-left relative">
                  {/* Play Button desktop - repositionné 4cm plus haut et 5cm à droite, agrandi de 60% */}
                  <div className="hidden md:block absolute -top-32 left-1/2 lg:left-[22rem] xl:left-[26rem] transform -translate-x-1/2 z-10">
                    <button
                      type="button"
                      data-audio-target="audio-methode"
                      aria-label="Écouter l'essentiel : 5 min"
                      className="relative select-none rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgba(255,106,0,0.55)]"
                      style={{ width: "140px", height: "140px", WebkitTapHighlightColor: "transparent" }}
                    >
                      <div className="absolute inset-0 rounded-full grid place-items-center animate-cw-breathe">
                        <div
                          className="rounded-full grid place-items-center bg-[rgba(255,106,0,0.08)] border border-[rgba(255,106,0,0.35)]"
                          style={{ width: "98px", height: "98px" }}
                        >
                          {isPlaying ? (
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="#FF6A00" aria-hidden="true">
                              <rect x="6" y="5" width="4" height="14" rx="1.2" />
                              <rect x="14" y="5" width="4" height="14" rx="1.2" />
                            </svg>
                          ) : (
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="#FF6A00" aria-hidden="true">
                              <polygon points="8,5 19,12 8,19" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="absolute inset-0 rounded-full pointer-events-none" style={{ boxShadow: "0 0 0 1px rgba(255,106,0,.28)" }} />
                    </button>
                  </div>
                  
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-inter font-bold leading-[1.1] tracking-tight">
                    <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                      Transformez votre
                    </span>
                    <br />
                    <span className="text-foreground">présence Amazon</span>
                  </h1>
                  
                  <p className="text-lg md:text-xl xl:text-2xl font-inter font-light text-muted-foreground leading-relaxed max-w-xl md:max-w-2xl xl:max-w-3xl mx-auto lg:mx-0">
                    Stratégie e-commerce Amazon et créativité de marque pour propulser votre croissance
                  </p>
                </div>
                
                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center lg:justify-start items-center mb-8">
                    <Button variant="cta-primary" size="xl" asChild>
                      <Link to="/audit-gratuit">Audit Amazon gratuit</Link>
                    </Button>
                    <Button variant="cta-secondary" size="xl" className="group" onClick={() => window.location.href = '/services'}>
                      <span>Découvrir nos services</span>
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar - Minimal & Dynamic */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-gradient-to-tl from-accent/5 to-transparent rounded-full blur-3xl animate-pulse" style={{
          animationDelay: '2s'
        }}></div>
        </div>
        
        <div className="container mx-auto px-6 xl:px-8 2xl:px-12 relative z-10">
          <div className="max-w-7xl 2xl:max-w-8xl mx-auto">
            <div className="flex flex-wrap justify-center items-center gap-16 md:gap-24 xl:gap-32 2xl:gap-40">
              {[{
              number: 10,
              suffix: "M+",
              label: "Revenue generated"
            }, {
              number: 20,
              suffix: "+",
              label: "Brands launched"
            }, {
              number: 98,
              suffix: "%",
              label: "Success rate"
            }, {
              number: 5,
              suffix: "★",
              label: "Client rating"
            }].map((stat, index) => <div key={index} className="group text-center transform hover:scale-110 transition-all duration-500 flex flex-col items-center" style={{
              animationDelay: `${index * 0.2}s`
            }}>
                  <div className="relative flex-shrink-0">
                    <AnimatedCounter end={stat.number} suffix={stat.suffix} duration={1500 + index * 150} />
                    
                    {/* Subtle underline */}
                    <div className="mt-2 h-px w-12 mx-auto bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  <div className="text-sm text-muted-foreground font-light mt-3 tracking-wide uppercase">
                    {stat.label}
                  </div>
                  
                  {/* Progress bar container - same height for all */}
                  <div className="mt-3 h-4 flex items-center justify-center">
                    {stat.suffix === "%" && <div className="w-20">
                        <div className="h-1 bg-border/30 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-primary to-primary-glow rounded-full animate-[width-fill_2s_ease-out_forwards]" style={{
                      width: '0%'
                    }}></div>
                        </div>
                      </div>}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Profile Tabs - Audit Section */}
      <ProfilesTabs />

      {/* Services Section - Dynamic with sophisticated color effects */}
      <section id="amazon" className="py-24 relative overflow-hidden">
        {/* Animated background gradients */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-gradient-to-tl from-accent/10 via-accent/5 to-transparent rounded-full blur-3xl animate-pulse" style={{
          animationDelay: '2s'
        }}></div>
          <div className="absolute top-1/2 left-0 w-64 h-64 bg-gradient-to-r from-secondary/10 via-secondary/5 to-transparent rounded-full blur-2xl animate-pulse" style={{
          animationDelay: '4s'
        }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mb-16 mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 via-primary/10 to-primary/5 border border-primary/30 text-primary text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-gradient-to-r from-primary to-accent rounded-full mr-2 animate-pulse"></span>
              Excellence Amazon & Performance
            </div>
            <h2 className="text-4xl font-bold mb-4 tracking-tight bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
              Services Amazon
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Solutions complètes pour optimiser votre présence et performance sur Amazon
            </p>
          </div>

          {/* Dashboard Preview Section */}
          <div className="mb-28 relative max-w-3xl mx-auto">
            <div className="relative group">
              {/* Glow effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 via-emerald-400/15 to-emerald-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-40"></div>
              
              {/* Image container with glassmorphism */}
              <div className="relative rounded-2xl overflow-hidden border border-emerald-500/20 backdrop-blur-sm bg-stone-900/30 p-2 shadow-2xl">
                <img 
                  src={dashboardImage} 
                  alt="Dashboard Amazon Cosmos Wave - Analytics et gestion produits" 
                  className="w-full h-auto rounded-xl transform group-hover:scale-[1.02] transition-transform duration-500"
                  loading="eager"
                />
              </div>
            </div>
            
            {/* Badges below image */}
            <div className="flex gap-3 justify-center mt-10">
              <div className="border border-primary/30 px-4 py-2 rounded-full text-xs font-medium text-primary">
                Analytics temps réel
              </div>
              <div className="border border-primary/30 px-4 py-2 rounded-full text-xs font-medium text-primary">
                Multi-catégories
              </div>
              <div className="border border-primary/30 px-4 py-2 rounded-full text-xs font-medium text-primary">
                Growth tracking
              </div>
            </div>

            {/* Demo Access Button */}
            <div className="flex justify-center mt-8">
              <Button
                onClick={handleDemoAccess}
                disabled={isDemoLoading}
                className="group relative px-8 py-6 text-base font-medium"
              >
                {isDemoLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Connexion en cours...
                  </>
                ) : (
                  <>
                    Accéder au Dashboard Démo
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {[{
            title: "Audit & Stratégie",
            description: "Analyse approfondie de votre écosystème Amazon avec plan d'action personnalisé",
            features: ["Audit SEO complet", "Analyse concurrentielle", "Benchmark performance", "Roadmap stratégique"],
            color: "from-primary/20 to-primary/5",
            borderColor: "border-primary/30",
            glowColor: "shadow-primary/20",
            icon: BarChart3
          }, {
            title: "Optimisation Listings",
            description: "Maximisation de la visibilité et conversion de vos fiches produits",
            features: ["SEO Amazon", "A+ Content", "Images premium", "Copywriting optimisé"],
            color: "from-accent/20 to-accent/5",
            borderColor: "border-accent/30",
            glowColor: "shadow-accent/20",
            icon: Target
          }, {
            title: "Gestion Publicitaire",
            description: "Campagnes Amazon Ads performantes avec suivi ROI en temps réel",
            features: ["Sponsored Ads", "Amazon DSP", "Optimisation budgets", "Reporting avancé"],
            color: "from-secondary/20 to-secondary/5",
            borderColor: "border-secondary/30",
            glowColor: "shadow-secondary/20",
            icon: Zap
          }, {
            title: "Expansion & Scale",
            description: "Croissance internationale et développement de votre catalogue",
            features: ["Marchés européens", "Stratégie FBA", "Nouveaux produits", "Automation"],
            color: "from-primary/15 via-accent/10 to-secondary/5",
            borderColor: "border-primary/20",
            glowColor: "shadow-primary/15",
            icon: Globe
          }].map((service, index) => <div key={index} className={`group relative bg-gradient-to-br ${service.color} border ${service.borderColor} rounded-2xl p-8 hover:shadow-2xl hover:${service.glowColor} transition-all duration-500 hover:scale-[1.02] backdrop-blur-sm`} style={{
            animationDelay: `${index * 0.1}s`
          }}>
                {/* Content container */}
                <div className="relative space-y-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold group-hover:text-primary transition-colors duration-300">
                      {service.title}
                    </h3>
                  </div>
                  <div>
                    <p className="text-[#D7DEAF] leading-relaxed group-hover:text-foreground/90 transition-colors duration-300">
                      {service.description}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {service.features.map((feature, fIndex) => <div key={fIndex} className="flex items-center text-sm transition-all duration-300">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full mr-3"></div>
                        <span className="text-primary font-medium">{feature}</span>
                      </div>)}
                  </div>
                </div>
              </div>)}
          </div>
          
          {/* Case Studies Preview */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold mb-4 text-foreground">
                Résultats concrets de nos clients
              </h3>
              <p className="text-muted-foreground">
                Découvrez l'impact de nos stratégies Amazon à travers nos études de cas
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Volume Case Study */}
              <div className="group relative bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-6 hover:shadow-xl hover:shadow-primary/10 transition-all duration-500 hover:scale-[1.02]">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-1">Volume de ventes</h4>
                    <p className="text-sm text-muted-foreground">Étude de cas · 23 mois</p>
                  </div>
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>
                
                <div className="h-32 mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={[
                      { name: 'Début', value: 0 },
                      { name: '12m', value: 14479 },
                      { name: '23m', value: 22199 }
                    ]}>
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary mb-1">+22K unités</p>
                  <p className="text-sm text-muted-foreground">vendues en 23 mois</p>
                </div>
              </div>
              
              {/* Revenue Case Study */}
              <div className="group relative bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20 rounded-2xl p-6 hover:shadow-xl hover:shadow-accent/10 transition-all duration-500 hover:scale-[1.02]">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-1">Chiffre d'affaires</h4>
                    <p className="text-sm text-muted-foreground">Étude de cas · 23 mois</p>
                  </div>
                  <BarChart3 className="w-6 h-6 text-accent" />
                </div>
                
                <div className="h-32 mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={[
                      { name: 'Début', value: 0 },
                      { name: '12m', value: 505342 },
                      { name: '23m', value: 704990 }
                    ]}>
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="hsl(var(--accent))" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="text-center">
                  <p className="text-2xl font-bold text-accent mb-1">705K€</p>
                  <p className="text-sm text-muted-foreground">de CA généré</p>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-12">
              <Button 
                variant="outline" 
                size="lg"
                className="group border-primary/30 hover:border-primary hover:bg-primary/10 transition-all duration-300"
                onClick={() => window.location.href = '/cases/amazon'}
              >
                <TrendingUp className="w-4 h-4 mr-2 group-hover:text-primary" />
                Voir toutes nos études Amazon
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Creative Studio - Clean Tech Design */}
      <section id="creative-studio" className="py-16 relative overflow-hidden">
        {/* Tech background pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-card/10 to-background"></div>
          <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `
                linear-gradient(90deg, hsl(var(--primary)) 0.5px, transparent 0.5px),
                linear-gradient(0deg, hsl(var(--primary)) 0.5px, transparent 0.5px)
              `,
          backgroundSize: '32px 32px'
        }}></div>
          {/* Single accent: scanline */}
          <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 via-transparent to-accent/10 border border-primary/20 text-primary text-sm font-medium mb-8">
              <div className="w-2 h-2 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
              <span>Creative Technology Studio</span>
              <div className="w-1 h-1 bg-accent rounded-full animate-pulse" style={{
              animationDelay: '0.5s'
            }}></div>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight leading-[1.1]">
              <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                Creative Studio
              </span>
            </h2>
            
            <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              De l'idée brute à la marque désirable
            </p>
          </div>
          
          {/* Asteroid Brand Image with subtle halo */}
          <div className="flex justify-center mt-12 mb-8">
            <div className="relative group">
              <img 
                src="/lovable-uploads/6edae097-e3d4-44e7-acae-9211e78f0bad.png" 
                alt="Cosmos Wave asteroid branding - De l'idée brute à la marque désirable"
                className="w-80 h-auto opacity-90 group-hover:opacity-100 transition-opacity duration-500 relative z-10"
                loading="lazy"
              />
              {/* Subtle green halo effect */}
              <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-primary/8 via-primary/12 to-accent/8 rounded-full blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
            </div>
          </div>
          
          {/* Services Grid */}
          <div className="grid lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {[{
            title: "Positionnement & récit",
            desc: "Plateforme de marque et angles d'attraction catégorie.",
            icon: "01",
            image: "/lovable-uploads/7baa3f13-cef5-493e-87f4-85ceebdbabde.png",
            gradient: "from-primary/10 to-primary/5",
            borderGradient: "from-primary/30 via-primary/10 to-transparent"
          }, {
            title: "Identité & systèmes",
            desc: "Logo, palettes, composants, guidelines multi‑canaux.",
            icon: "02",
            image: "/lovable-uploads/71ffa2f2-03f6-46f0-8c21-a972c6d830c5.png",
            gradient: "from-accent/10 to-accent/5",
            borderGradient: "from-accent/30 via-accent/10 to-transparent"
          }, {
            title: "Packaging & preuves",
            desc: "Architecture de gamme, claims, bénéfices visuels, éléments de confiance.",
            icon: "03",
            image: "/lovable-uploads/27f4aea0-7776-4b6c-be1d-1d4de5bdf52c.png",
            gradient: "from-secondary/10 to-secondary/5",
            borderGradient: "from-secondary/30 via-secondary/10 to-transparent"
          }].map((service, index) => <div key={index} className={`group relative bg-gradient-to-br ${service.gradient} border border-border/20 rounded-3xl p-4 hover:shadow-2xl hover:shadow-primary/5 transition-all duration-700 hover:scale-[1.02]`} style={{
            animationDelay: `${index * 0.2}s`
          }}>
                {/* Content container */}
                <div className="relative border border-neutral-800 hover:border-primary rounded-xl p-4 transition-colors h-full flex flex-col">
                  {/* Icon number */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-card/50 flex items-center justify-center">
                      <span className="text-lg font-bold text-primary">
                        {service.icon}
                      </span>
                    </div>
                  </div>
                  
                  {/* Centered Image */}
                  <div className="flex-1 flex items-center justify-center mb-6">
                    <div className="w-64 h-64 flex items-center justify-center">
                      <img src={service.image} alt={`${service.title} - Service créatif Cosmos Wave pour optimisation Amazon et branding`} className="w-full h-full object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-300" loading="lazy" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold leading-tight text-foreground">
                      {service.title}
                    </h3>
                    
                    <p className="text-[#D7DEAF] leading-relaxed">
                      {service.desc}
                    </p>
                  </div>
                  
                  {/* Tech progress bar */}
                  <div className="mt-6 h-1 bg-border/20 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary to-accent rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000" style={{
                  transitionDelay: `${index * 300}ms`
                }}></div>
                  </div>
                </div>
              </div>)}
          </div>
          
          {/* Bottom tech signature */}
          <div className="flex items-center justify-center mt-16 gap-3">
            <div className="h-px w-8 bg-gradient-to-r from-transparent to-primary/30"></div>
            <div className="flex items-center gap-2">
              <div className="w-1 h-1 bg-primary/40 rounded-full animate-pulse"></div>
              <span className="text-xs font-mono text-muted-foreground tracking-wider">CREATIVE.TECH.STUDIO</span>
              <div className="w-1 h-1 bg-accent/40 rounded-full animate-pulse" style={{
              animationDelay: '0.5s'
            }}></div>
            </div>
            <div className="h-px w-8 bg-gradient-to-l from-transparent to-primary/30"></div>
          </div>
        </div>
      </section>

      {/* Cases */}
      <section id="cas" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
        {/* Header - Improved Mobile Layout */}
        <div className="mb-12 animate-fade-in">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-6 lg:mb-8">
              Ce que nos clients ont gagné
            </h2>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {[{
          id: "case_beaute_capillaire",
          title: "Beauté capillaire — DNVB",
          tag: "Beauté",
          summary_lines: ["Défi – Produit noyé dans le mass merch.", "Intervention – Renaming + identité clean-beauty + A+.", "Résultat – Devient repère « cheveux sains » en rayon digital."],
          cta_label: "Voir le portfolio complet (NDA)",
          cta_href: "/cas/beaute-capillaire-dnvb"
        }, {
          id: "case_food_export",
          title: "Food — Export premium",
          tag: "Food",
          summary_lines: ["Défi – Codes locaux limitants à l'export.", "Intervention – Minimalisme crème + flacon élancé + storytelling terroir.", "Résultat – Export-ready en épicerie fine."],
          cta_label: "Voir le portfolio complet (NDA)",
          cta_href: "/cas/food-export-premium"
        }, {
          id: "case_tech_logistique",
          title: "Tech logistique — B2B",
          tag: "Tech",
          summary_lines: ["Défi – Plateforme sans identité claire.", "Intervention – Système visuel crédible + clarif. proposition de valeur.", "Résultat – Référence lors d'appels d'offres."],
          cta_label: "Voir le portfolio complet (NDA)",
          cta_href: "/cas/tech-logistique-b2b"
        }].map((caseItem, i) => <Card key={caseItem.id} className="p-6 border border-neutral-800 hover:border-primary rounded-xl transition-colors group animate-fade-in relative overflow-hidden flex flex-col" style={{
          animationDelay: `${i * 0.1}s`
        }}>
              {/* Gradient hover effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">{caseItem.title}</h3>
                  <Badge variant="outline" className="text-primary border-primary/30 bg-primary/10 group-hover:border-primary/50 transition-colors">
                    {caseItem.tag}
                  </Badge>
                </div>
                <div className="space-y-2 mb-6 flex-1">
                  {caseItem.summary_lines.map((line, index) => <p key={index} className="text-sm text-muted-foreground leading-relaxed">{line}</p>)}
                </div>
                
                {/* Enhanced CTA */}
                <div className="pt-4 border-t border-border/30 mt-auto">
                  <Button 
                    variant="cta-outline" 
                    size="lg" 
                    className="w-full group/cta justify-between hover:bg-primary/10"
                    onClick={() => navigate(caseItem.cta_href)}
                  >
                    <span className="font-medium">Voir l'étude de cas</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs opacity-70">(NDA)</span>
                      <ExternalLink className="w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                    </div>
                  </Button>
                </div>
              </div>
            </Card>)}
        </div>
        
        <p className="text-sm text-muted-foreground animate-fade-in">
          Aperçu non exhaustif — nombreux projets sous NDA pour préserver l'avantage compétitif des marques.
        </p>
      </section>

      {/* Method - Redesigned */}
      <section id="method" className="relative py-24 overflow-hidden">
        {/* Sophisticated background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/2 to-background"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }}></div>
          {/* Animated gradient orbs */}
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl animate-pulse opacity-40"></div>
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-gradient-to-tl from-accent/5 to-transparent rounded-full blur-3xl animate-pulse opacity-40" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Enhanced Header */}
          <div className="text-center max-w-4xl mx-auto mb-20">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-primary/10 via-transparent to-accent/10 border border-primary/20 text-primary text-sm font-medium mb-8 animate-fade-in">
              <div className="w-2 h-2 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent font-semibold">Notre approche</span>
              <div className="w-1 h-1 bg-accent rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            </div>
            
            <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight leading-[0.9] animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                Méthode
              </span>
            </h2>
          </div>

          {/* Enhanced Process Visualization */}
          <div className="relative max-w-6xl mx-auto mb-20 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 items-center">
              
              {/* Data Phase */}
              <div className="relative group">
                <div className="relative bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border border-border/50 rounded-3xl p-8 hover:border-primary/50 transition-all duration-700 hover:scale-[1.02] h-64 flex flex-col justify-center items-center">
                  {/* Animated icon */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 relative">
                      {/* Outer rotating ring */}
                      <div className="absolute inset-0 border-2 border-primary/20 rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
                      <div className="absolute inset-2 border border-primary/30 rounded-full animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}></div>
                      {/* Core */}
                      <div className="absolute inset-6 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-primary rounded-full animate-pulse"></div>
                      </div>
                      {/* Orbiting particles */}
                      <div className="absolute top-0 left-1/2 w-2 h-2 -ml-1 bg-primary/60 rounded-full animate-bounce"></div>
                      <div className="absolute top-1/2 right-0 w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '0.33s' }}></div>
                      <div className="absolute bottom-0 left-1/2 w-1 h-1 -ml-0.5 bg-primary/30 rounded-full animate-bounce" style={{ animationDelay: '0.66s' }}></div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-primary mb-2 tracking-wider">DATA</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">Analyse approfondie des performances et opportunités</p>
                  </div>
                  
                  {/* Progress indicator */}
                  <div className="absolute bottom-4 left-4 right-4 h-1 bg-border/30 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary to-accent rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000"></div>
                  </div>
                </div>
                
                {/* Connection line - visible on desktop */}
                <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-px">
                  <div className="h-full bg-gradient-to-r from-primary/40 to-transparent"></div>
                  <div className="absolute top-0 right-0 w-2 h-2 -mt-1 border-r border-t border-primary/40 rotate-45"></div>
                </div>
              </div>

              {/* Design Phase */}
              <div className="relative group">
                <div className="relative bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border border-border/50 rounded-3xl p-8 hover:border-primary/50 transition-all duration-700 hover:scale-[1.02] h-64 flex flex-col justify-center items-center">
                  {/* Enhanced geometric design */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 relative">
                      {/* Layered diamonds */}
                      <div className="absolute inset-0 rotate-45 border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-transparent transform transition-transform duration-1000 group-hover:rotate-[60deg] group-hover:scale-110"></div>
                      <div className="absolute inset-3 rotate-45 border border-primary/40 bg-gradient-to-tl from-primary/5 to-accent/5 transform transition-transform duration-1000 group-hover:rotate-[30deg]"></div>
                      <div className="absolute inset-6 rotate-45 bg-gradient-to-br from-primary/20 to-accent/20 transform transition-transform duration-1000 group-hover:rotate-[90deg]"></div>
                      {/* Central pulse */}
                      <div className="absolute top-1/2 left-1/2 w-3 h-3 -mt-1.5 -ml-1.5 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-primary mb-2 tracking-wider">DESIGN</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">Création d'expériences visuelles impactantes</p>
                  </div>
                  
                  <div className="absolute bottom-4 left-4 right-4 h-1 bg-border/30 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-accent to-primary rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000" style={{ transitionDelay: '0.3s' }}></div>
                  </div>
                </div>
                
                <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-px">
                  <div className="h-full bg-gradient-to-r from-primary/40 to-transparent"></div>
                  <div className="absolute top-0 right-0 w-2 h-2 -mt-1 border-r border-t border-primary/40 rotate-45"></div>
                </div>
              </div>

              {/* Delivery Phase */}
              <div className="relative group">
                <div className="relative bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border border-border/50 rounded-3xl p-8 hover:border-primary/50 transition-all duration-700 hover:scale-[1.02] h-64 flex flex-col justify-center items-center">
                  {/* Advanced hexagonal structure */}
                  <div className="relative mb-6">
                    <div className="w-20 h-20 relative transform transition-transform duration-700 group-hover:scale-110">
                      <svg className="w-full h-full" viewBox="0 0 80 80">
                        {/* Outer hexagon */}
                        <polygon
                          points="40,8 60,22 60,58 40,72 20,58 20,22"
                          fill="none"
                          stroke="hsl(var(--primary) / 0.3)"
                          strokeWidth="2"
                          className="animate-pulse"
                        />
                        {/* Middle hexagon */}
                        <polygon
                          points="40,16 54,26 54,54 40,64 26,54 26,26"
                          fill="hsl(var(--primary) / 0.05)"
                          stroke="hsl(var(--primary) / 0.4)"
                          strokeWidth="1.5"
                        />
                        {/* Inner hexagon */}
                        <polygon
                          points="40,24 48,30 48,50 40,56 32,50 32,30"
                          fill="hsl(var(--accent) / 0.1)"
                          stroke="hsl(var(--accent) / 0.5)"
                          strokeWidth="1"
                        />
                      </svg>
                      {/* Central core */}
                      <div className="absolute top-1/2 left-1/2 w-4 h-4 -mt-2 -ml-2 bg-gradient-to-br from-primary to-accent rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-primary mb-2 tracking-wider">DELIVERY</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">Exécution précise et résultats mesurables</p>
                  </div>
                  
                  <div className="absolute bottom-4 left-4 right-4 h-1 bg-border/30 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-primary to-accent rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000" style={{ transitionDelay: '0.6s' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[{
              icon: Search,
              number: "01",
              title: "Diagnostiquer",
              desc: "Audit SEO sémantique, analyse concurrentielle, évaluation des opérations. Feuille de route stratégique priorisée.",
              color: "from-primary/20 to-primary/5",
              iconColor: "text-primary"
            }, {
              icon: Wrench,
              number: "02", 
              title: "Construire",
              desc: "Optimisation des listings, création A+, développement Store, production d'assets. Standards de qualité et cohérence maximale.",
              color: "from-accent/20 to-accent/5",
              iconColor: "text-accent"
            }, {
              icon: Megaphone,
              number: "03",
              title: "Amplifier", 
              desc: "Déploiement Ads, activation UGC, expansion marchés. Pilotage data-driven avec objectifs clairs et mesurables.",
              color: "from-secondary/20 to-secondary/5",
              iconColor: "text-secondary"
            }, {
              icon: TrendingUp,
              number: "04",
              title: "Opérer",
              desc: "Gestion FBA/FBM, optimisation Buy Box, surveillance account health, résolution litiges. SLA garantis et reporting détaillé.",
              color: "from-primary/15 to-accent/5",
              iconColor: "text-primary"
            }].map(({ icon: Icon, number, title, desc, color, iconColor }, i) => (
              <div key={title} className={`group relative bg-gradient-to-br ${color} border border-border/50 rounded-2xl p-6 hover:border-primary/30 hover:shadow-lg transition-all duration-500 hover:scale-[1.02] animate-fade-in`} style={{ animationDelay: `${0.6 + i * 0.1}s` }}>
                
                {/* Number badge */}
                <div className="absolute -top-3 -left-3 w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg">
                  {number}
                </div>
                
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-card/80 to-card/40 flex items-center justify-center border border-border/30 group-hover:border-primary/30 transition-colors`}>
                    <Icon className={`w-6 h-6 ${iconColor} group-hover:scale-110 transition-transform`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg group-hover:text-primary transition-colors mb-2">{title}</h3>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                
                {/* Hover effect indicator */}
                <div className="absolute bottom-3 right-3 w-2 h-2 bg-primary/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section id="insights" className="bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-12 animate-fade-in">
            FAQ - Questions fréquentes
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            {[{
            question: "Quel est votre périmètre d'accompagnement Amazon ?",
            answer: "Audit & stratégie, optimisation SEO listings, A+ & Store, Ads, opérations FBA/FBM & Buy Box, Brand Registry et expansion UE/US."
          }, {
            question: "Combien de temps pour voir des effets ?",
            answer: "Nous priorisons des quick wins (1–4 semaines) et une trajectoire soutenue (3–6 mois), selon l'adhérence aux recommandations et l'état initial."
          }, {
            question: "Travaillez‑vous sous NDA et sans divulguer de prix ?",
            answer: "Oui. La confidentialité est au cœur de notre méthode et nos propositions sont sur‑mesure."
          }, {
            question: "Aidez‑vous au dépôt de marque / Brand Registry ?",
            answer: "Oui. Éligibilité, activation, protection IP, signalements, exploitation Brand Analytics."
          }, {
            question: "Qu'est-ce que le Digital Shelf et pourquoi est-il crucial pour mon entreprise ?",
            answer: "Le Digital Shelf regroupe tous les espaces où vos produits sont visibles et vendus (marketplaces, site e-commerce, réseaux sociaux, publicités). Mieux il est optimisé, plus la visibilité, le trafic et les ventes progressent. Nous travaillons chaque levier pour vous démarquer."
          }, {
            question: "Comment Cosmos Wave se différencie des autres agences digitales ?",
            answer: "Expertise pointue marketplaces et social, approche omnicanale personnalisée qui aligne marketplaces, e-commerce et campagnes. Résultats mesurables et concrets."
          }, {
            question: "Quels résultats puis-je attendre en collaborant avec Cosmos Wave ?",
            answer: "Une croissance significative et durable des ventes grâce à une stratégie optimisée. Exemple de trajectoire : passage de 0 à >700 000 € de CA cumulé en 23 mois (cas détaillé sous NDA)."
          }, {
            question: "Quels types d'entreprises accompagnez-vous ?",
            answer: "De l'auto-entrepreneur à la grande entreprise. Notre méthode s'adapte à votre maturité digitale et à vos objectifs — lancement, structuration ou scale."
          }].map(({
            question,
            answer
          }, i) => <Card key={i} className="border-border bg-card backdrop-blur-sm animate-fade-in" style={{
            animationDelay: `${i * 0.1}s`
          }}>
                <button onClick={() => toggleFaq(i)} className="w-full p-6 text-left font-medium hover:text-primary transition-colors focus:outline-none">
                  <div className="flex items-center justify-between">
                    <span>{question}</span>
                    <span className={`transition-transform duration-300 ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                  </div>
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-muted-foreground leading-relaxed animate-fade-in">
                    {answer}
                  </div>}
              </Card>)}
          </div>
        </div>
      </section>


      {/* Contact */}
      <section id="contact" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[var(--gradient-subtle)] pointer-events-none" />
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" style={{
          animationDelay: '1s'
        }} />
        </div>
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="animate-fade-in">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Parlons de votre croissance Amazon
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Réservez un audit de 30 min. Nous analysons votre catégorie, vos listings, votre compte et vous repartirez avec une feuille de route priorisée.
              </p>
              <ul className="space-y-3 text-foreground">
                {["Audit vidéo possible", "Modèle de roadmap offert", "Canal WhatsApp dédié"].map((item, i) => <li key={item} className="flex items-center gap-3 animate-slide-in" style={{
                animationDelay: `${i * 0.1}s`
              }}>
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
            
            <div className="flex justify-center animate-fade-in" style={{
            animationDelay: '0.3s'
          }}>
              <Button 
                variant="cta-primary" 
                size="xl" 
                className="group"
                onClick={() => navigate("/audit-gratuit")}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Planifier votre RDV
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Destinations Navigation */}
      <DestinationsNav />

      {/* Footer */}
      <footer className="relative overflow-hidden bg-gradient-to-b from-card/20 to-background border-t border-border/50">
        <div className="container mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-4 gap-8 mb-12">
            {/* Description only - Logo removed */}
            <div className="lg:col-span-2">
              <p className="text-muted-foreground leading-relaxed max-w-md mx-auto lg:mx-0 text-center lg:text-left">
                Votre partenaire Amazon pour accélérer, structurer et optimiser votre présence e-commerce. 
                Expertise technique et créative au service de votre croissance.
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="font-semibold text-foreground mb-4">Navigation</h4>
              <ul className="space-y-3">
                <li><a href="/services" className="text-muted-foreground hover:text-primary transition-colors">Services</a></li>
                <li><a href="/cases" className="text-muted-foreground hover:text-primary transition-colors">Cases Studies</a></li>
                <li><a href="/industriels-fabricants" className="text-muted-foreground hover:text-primary transition-colors">Industriels</a></li>
                <li><a href="/marques-dnvb" className="text-muted-foreground hover:text-primary transition-colors">DNVB & Marques</a></li>
                <li><a href="/llm-facts" className="text-muted-foreground hover:text-primary transition-colors">LLM Facts</a></li>
                <li><a href="/admin" className="text-muted-foreground hover:text-primary transition-colors">CWLab</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-foreground mb-4">Contact</h4>
              <ul className="space-y-3">
                <li><Link to="/audit-gratuit" className="text-muted-foreground hover:text-primary transition-colors">Planifier un RDV</Link></li>
                <li><a href="mailto:contact@cosmos-wave.com" className="text-muted-foreground hover:text-primary transition-colors">contact@cosmos-wave.com</a></li>
                <li>
                  <a 
                    href="https://www.linkedin.com/company/cosmoswave" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border/20 pt-8 text-center">
            <p className="text-muted-foreground text-sm">
              © 2024 Cosmos Wave. Tous droits réservés. | 
              <a href="/mentions-legales" className="hover:text-primary transition-colors ml-1">Mentions légales</a> | 
              <a href="/politique-confidentialite" className="hover:text-primary transition-colors ml-1">Politique de confidentialité</a> | 
              <a id="cw-manage-cookies" href="#" rel="nofollow" className="hover:text-primary transition-colors ml-1">Gérer mes cookies</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}